// The running transcript, under the call.
//
// Not speaker-attributed (spec s2): we care what information surfaced, not who
// said it, and the stream is not diarized. The left column carries the
// segment's timestamp instead, which keeps the prototype's two-column rhythm
// without inventing an attribution the pipeline cannot support.
//
// Partials appear here and nowhere else - the extraction loop consumes finals
// exclusively (spec s5.1).

import { useEffect, useRef } from 'react';
import type { TranscriptSegment } from '../pipeline/Transcriber';

type Props = {
  segments: TranscriptSegment[];
  partial: string;
  sourceNote: string;
};

/**
 * How much history the panel renders. The buffer itself is never truncated
 * (spec s5.2) - this only bounds the DOM.
 *
 * Six was right when the list could not scroll and only the tail was ever
 * visible. Now that it scrolls on its own, keeping more is what makes scrolling
 * back worth doing.
 */
const VISIBLE_SEGMENTS = 60;

function stamp(ms: number): string {
  const total = Math.floor(ms / 1000);
  return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
}

export function TranscriptView({ segments, partial, sourceNote }: Props) {
  const listRef = useRef<HTMLDivElement | null>(null);
  // Whether to keep following the tail. Set from the user's own scrolling, not
  // recomputed after new content arrives - by then the measurement would
  // already include the line we just added.
  const following = useRef(true);
  const visible = segments.slice(-VISIBLE_SEGMENTS);

  useEffect(() => {
    const node = listRef.current;
    if (!node || !following.current) return;
    // scrollTop on the list, never scrollIntoView: that walks up to the nearest
    // scrollable ancestor, which is the page, and yanks the whole thing down on
    // every transcript update.
    node.scrollTop = node.scrollHeight;
  }, [segments.length, partial]);

  return (
    <div className="feed">
      <div className="feedhead">
        <span className="eyebrow">Transcript</span>
        <span className="src">{sourceNote}</span>
      </div>

      <div
        className="feedlist"
        ref={listRef}
        onScroll={() => {
          const node = listRef.current;
          if (!node) return;
          // Scrolling up to read stops the follow; returning to the bottom
          // resumes it.
          following.current = node.scrollHeight - node.scrollTop - node.clientHeight < 40;
        }}
      >
        {visible.length === 0 && !partial && (
          <p className="empty-note">Start listening to watch the checklist fill.</p>
        )}

        {visible.map((segment, i) => (
          <div className="utt" key={`${segment.startMs}-${i}`}>
            <span className="at">{stamp(segment.startMs)}</span>
            <span className="said">{segment.text}</span>
          </div>
        ))}

        {partial && (
          <div className="utt partial">
            <span className="at" />
            <span className="said">{partial}</span>
          </div>
        )}
      </div>
    </div>
  );
}
